import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "react-select";

// Define the interface for the duration option
interface DurationOption {
  value: string;
  label: string;
}

// Define the interface for the component props
interface SelectionScheduleData {
  selectedSlots: Set<string>;
  duration: DurationOption | null;
}

interface WeeklyScheduleProps {
  initialData?: SelectionScheduleData;
  onDataChange: (data: SelectionScheduleData) => void;
  durationOptions?: DurationOption[];
}

const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Styled dropdown
const customSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    borderRadius: 8,
    borderColor: "#d1d5db",
    padding: 2,

    boxShadow: "none",
    "&:hover": { borderColor: "#a1a1aa" },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    padding: 10,
    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.15)",
    width: "95%",
    margin: 6,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    borderRadius: 8,
    backgroundColor: state.isSelected ? "#eff6ff" : "#ffffff",
    color: "#1f2937",
    padding: 12,
    marginBottom: 8,
    cursor: "pointer",
    transition: "background-color 0.2s",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#1f2937",
  }),
};

const WeeklySchedule: React.FC<WeeklyScheduleProps> = ({
  initialData,
  onDataChange,
  durationOptions = [
    { value: "10 sec", label: "10 sec" },
    { value: "20 sec", label: "20 sec" },
    { value: "30 sec", label: "30 sec" },
  ],
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    initialData?.selectedSlots || new Set()
  );
  const [duration, setDuration] = useState<DurationOption | null>(
    initialData?.duration || null
  );
  const [visualState, setVisualState] = useState<Set<string>>(
    initialData?.selectedSlots || new Set()
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<string | null>(null);
  const [selectionPreview, setSelectionPreview] = useState<Set<string>>(
    new Set()
  );
  const [selectionMode, setSelectionMode] = useState<"add" | "remove">("add");
  const [hasMoved, setHasMoved] = useState(false);

  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced data change to prevent excessive updates
  const debouncedOnDataChange = useCallback(
    (data: SelectionScheduleData) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onDataChange(data);
      }, 50);
    },
    [onDataChange]
  );

  // Update parent whenever selected state or duration changes
  useEffect(() => {
    const data: SelectionScheduleData = {
      selectedSlots: new Set(selected),
      duration,
    };
    debouncedOnDataChange(data);
  }, [selected, duration, debouncedOnDataChange]);

  // Keep visual state in sync with actual state
  useEffect(() => {
    setVisualState(new Set(selected));
  }, [selected]);

  // Update local state when initialData changes
  useEffect(() => {
    if (initialData) {
      if (initialData.selectedSlots) {
        const newSlots = new Set(initialData.selectedSlots);
        setSelected(newSlots);
        setVisualState(newSlots);
      }
      // Handle duration including null values
      setDuration(initialData.duration);
    }
  }, [initialData]);

  // Helper function to get slot coordinates
  const getSlotCoordinates = (key: string) => {
    const [day, hour] = key.split("-");
    const dayIndex = days.indexOf(day);
    const hourIndex = parseInt(hour);
    return { dayIndex, hourIndex };
  };

  // Helper function to create key from coordinates
  const createKey = (dayIndex: number, hourIndex: number) => {
    return `${days[dayIndex]}-${String(hourIndex).padStart(2, "0")}`;
  };

  // Get slots in rectangular selection
  const getSlotsInSelection = (start: string, end: string) => {
    const startCoords = getSlotCoordinates(start);
    const endCoords = getSlotCoordinates(end);

    const minDay = Math.min(startCoords.dayIndex, endCoords.dayIndex);
    const maxDay = Math.max(startCoords.dayIndex, endCoords.dayIndex);
    const minHour = Math.min(startCoords.hourIndex, endCoords.hourIndex);
    const maxHour = Math.max(startCoords.hourIndex, endCoords.hourIndex);

    const slots = new Set<string>();
    for (let day = minDay; day <= maxDay; day++) {
      for (let hour = minHour; hour <= maxHour; hour++) {
        slots.add(createKey(day, hour));
      }
    }
    return slots;
  };

  const handleMouseDown = (e: React.MouseEvent, day: string, hour: string) => {
    e.preventDefault();
    const key = `${day}-${hour}`;

    startPosRef.current = { x: e.clientX, y: e.clientY };
    setSelectionStart(key);
    setHasMoved(false);
    setIsSelecting(true);

    const mode = selected.has(key) ? "remove" : "add";
    setSelectionMode(mode);

    const newVisualState = new Set(visualState);
    if (mode === "add") {
      newVisualState.add(key);
    } else {
      newVisualState.delete(key);
    }
    setVisualState(newVisualState);

    setSelectionPreview(new Set([key]));
  };

  const handleMouseEnter = (day: string, hour: string) => {
    if (!isSelecting || !selectionStart || !startPosRef.current) return;

    const key = `${day}-${hour}`;

    if (key !== selectionStart && !hasMoved) {
      setHasMoved(true);
      setVisualState(new Set(selected));
    }

    if (hasMoved) {
      const slotsInSelection = getSlotsInSelection(selectionStart, key);
      setSelectionPreview(slotsInSelection);
    }
  };

  const handleMouseUp = () => {
    if (!isSelecting || !selectionStart) {
      setIsSelecting(false);
      return;
    }

    if (!hasMoved) {
      const key = selectionStart;
      setSelected((prev) => {
        const newSet = new Set(prev);
        if (selectionMode === "add") {
          newSet.add(key);
        } else {
          newSet.delete(key);
        }
        return newSet;
      });
    } else {
      setSelected((prev) => {
        const newSet = new Set(prev);
        selectionPreview.forEach((slot) => {
          if (selectionMode === "add") {
            newSet.add(slot);
          } else {
            newSet.delete(slot);
          }
        });
        return newSet;
      });
    }

    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionPreview(new Set());
    setHasMoved(false);
    startPosRef.current = null;
  };

  const handleMouseLeave = () => {
    if (isSelecting) {
      setVisualState(new Set(selected));
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionPreview(new Set());
      setHasMoved(false);
      startPosRef.current = null;
    }
  };

  const getButtonClass = (key: string) => {
    const isVisuallySelected = visualState.has(key);
    const isInPreview = selectionPreview.has(key) && hasMoved;

    if (isInPreview && isSelecting) {
      if (selectionMode === "add") {
        return "bg-[#87CEEB] border-2 border-blue-600";
      } else {
        return "bg-[#FFB6C1] border-2 border-red-600";
      }
    }

    if (isVisuallySelected) {
      return "bg-[#CCF0FB] border border-white";
    }

    return "bg-[#E4E4E4] opacity-30 border border-white";
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        handleMouseUp();
      }
    };

    if (isSelecting) {
      document.addEventListener("mouseup", handleGlobalMouseUp);
      return () => {
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [
    isSelecting,
    selectionStart,
    selectionPreview,
    selectionMode,
    selected,
    hasMoved,
  ]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleDurationChange = (selected: DurationOption | null) => {
    setDuration(selected);
  };

  return (
    <div
      className="w-full flex flex-col gap-4 text-white mb-4"
      style={{ fontFamily: "sans-serif" }}
    >
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          Duration<span className="text-red-500">*</span>:
        </label>
        <Select
          options={durationOptions}
          value={duration}
          onChange={handleDurationChange}
          styles={customSelectStyles}
          placeholder="Select Duration"
          isClearable
        />
      </div>
      <div className="text-black text-sm font-medium">
        Select days & time<span className="text-red-500">*</span> :
      </div>

      <div
        className="grid grid-cols-[48px_repeat(7,1fr)]"
        onMouseLeave={handleMouseLeave}
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
      >
        <div className="bg-white"></div>
        {days.map((day) => (
          <div
            key={day}
            className="bg-white text-black text-xs font-semibold text-center py-1"
          >
            {day}
          </div>
        ))}

        {hours.map((hour) => (
          <React.Fragment key={hour}>
            <div className="bg-white text-black text-xs font-medium text-center py-1">
              {hour}
            </div>
            {days.map((day) => {
              const key = `${day}-${hour}`;
              return (
                <div
                  key={key}
                  className={`w-full h-[22px] rounded-[4px] cursor-pointer ${getButtonClass(
                    key
                  )}`}
                  onMouseDown={(e) => handleMouseDown(e, day, hour)}
                  onMouseEnter={() => handleMouseEnter(day, hour)}
                  style={{
                    userSelect: "none",
                    WebkitUserSelect: "none",
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {isSelecting && hasMoved && (
        <div className="text-black text-xs mt-1 font-medium">
          {selectionMode === "add" ? "🔵 Adding" : "🔴 Removing"}{" "}
          {selectionPreview.size} slot
          {selectionPreview.size !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

export default WeeklySchedule;
